import React, { useMemo } from "react";
import { useGetUserProfilePicQuery } from "../app/store/authApiSlice";
import Moment from "react-moment";
import useDocDownload from "../app/hooks/useDocDownload";

const MESSAGING_LIST_STYLES = {
  messageRow: {
    display: "flex",
    width: "100%",
    marginBottom: "16px",
    padding: "0 8px",
    transition: "all 0.2s ease",
  },
  sendRow: {
    flexDirection: "row-reverse",
  },
  receiveRow: {
    flexDirection: "row",
  },
  avatarWrap: {
    display: "flex",
    alignItems: "flex-end",
    paddingBottom: "4px",
  },
  avatar: {
    width: "38px",
    height: "38px",
    borderRadius: "12px",
    objectFit: "cover",
    border: "2px solid #ffffff",
    boxShadow: "0 4px 12px rgba(15, 23, 42, 0.12)",
    flexShrink: 0,
    transition: "transform 0.2s ease",
  },
  bubbleWrap: {
    maxWidth: "85%",
    display: "flex",
    flexDirection: "column",
    margin: "0 10px",
  },
  sendBubbleWrap: {
    alignItems: "flex-end",
  },
  receiveBubbleWrap: {
    alignItems: "flex-start",
  },
  bubble: {
    padding: "12px 16px",
    fontSize: "14.5px",
    lineHeight: "1.55",
    position: "relative",
    boxShadow: "0 4px 15px rgba(15, 23, 42, 0.06)",
    wordBreak: "break-word",
    minWidth: "100px",
    transition: "all 0.2s ease",
  },
  sendBubble: {
    background: "linear-gradient(135deg, #3b82f6 0%, #4f46e5 100%)",
    color: "#ffffff",
    borderRadius: "20px 20px 4px 20px",
  },
  receiveBubble: {
    background: "#ffffff",
    color: "#1e293b",
    borderRadius: "20px 20px 20px 4px",
    border: "1px solid rgba(226, 232, 240, 0.8)",
  },
  authorName: {
    fontSize: "10px",
    fontWeight: 800,
    marginBottom: "4px",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  sendAuthor: {
    color: "#4f46e5",
    marginRight: "6px",
  },
  receiveAuthor: {
    color: "#64748b",
    marginLeft: "6px",
  },
  content: {
    marginBottom: "4px",
  },
  meta: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "6px",
    marginTop: "4px",
    height: "16px",
  },
  time: {
    fontSize: "10px",
    fontWeight: 600,
  },
  sendTime: {
    color: "rgba(255, 255, 255, 0.8)",
  },
  receiveTime: {
    color: "#94a3b8",
  },
  statusIcon: {
    fontSize: "12px",
    display: "flex",
  },
  readIcon: {
    color: "#bfdbfe",
  },
  unreadIcon: {
    color: "rgba(255, 255, 255, 0.5)",
  },
  attachmentList: {
    listStyle: "none",
    padding: 0,
    margin: "10px 0",
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  downloadButton: {
    fontSize: "12.5px",
    padding: "8px 12px",
    borderRadius: "12px",
    background: "rgba(255, 255, 255, 0.15)",
    color: "#ffffff",
    border: "1px solid rgba(255, 255, 255, 0.25)",
    display: "inline-flex",
    alignItems: "center",
    gap: "10px",
    transition: "all 0.2s ease",
    width: "100%",
    textAlign: "left",
    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
  },
  receiveDownloadButton: {
    background: "#f1f5f9",
    color: "#2563eb",
    border: "1px solid #e2e8f0",
  },
};

const MessagingList = ({ message, profilePicData }) => {
  const [handleDownloadFile] = useDocDownload();

  const isSend = message?.author === "send";

  // Fetch the sender's profile picture dynamically if received
  const { data: senderProfilePic } = useGetUserProfilePicQuery(
    message?.fromId,
    { skip: isSend || !message?.fromId }
  );

  const profilePicSrc = useMemo(() => {
    const defaultAvatar = "/assets/img/theme/team-4.png";
    if (isSend) {
      return profilePicData || defaultAvatar;
    }
    return senderProfilePic ? "data:image/jpeg;base64," + senderProfilePic : defaultAvatar;
  }, [isSend, profilePicData, senderProfilePic]);

  if (!message) return null;

  return (
    <div
      className="message-row-hover"
      style={{
        ...MESSAGING_LIST_STYLES.messageRow,
        ...(isSend ? MESSAGING_LIST_STYLES.sendRow : MESSAGING_LIST_STYLES.receiveRow),
      }}
    >
      <div style={MESSAGING_LIST_STYLES.avatarWrap}>
        <img
          alt={message.performer}
          className="chat-avatar"
          style={MESSAGING_LIST_STYLES.avatar}
          src={profilePicSrc}
        />
      </div>

      <div
        style={{
          ...MESSAGING_LIST_STYLES.bubbleWrap,
          ...(isSend ? MESSAGING_LIST_STYLES.sendBubbleWrap : MESSAGING_LIST_STYLES.receiveBubbleWrap),
        }}
      >
        <span
          style={{
            ...MESSAGING_LIST_STYLES.authorName,
            ...(isSend ? MESSAGING_LIST_STYLES.sendAuthor : MESSAGING_LIST_STYLES.receiveAuthor),
          }}
        >
          {message.performer}
        </span>

        <div
          className={isSend ? "send-bubble-text chat-bubble-elevated" : "chat-bubble-elevated"}
          style={{
            ...MESSAGING_LIST_STYLES.bubble,
            ...(isSend ? MESSAGING_LIST_STYLES.sendBubble : MESSAGING_LIST_STYLES.receiveBubble),
          }}
        >
          <div
            className="chat-content-reset"
            style={MESSAGING_LIST_STYLES.content}
            dangerouslySetInnerHTML={{ __html: message.data }}
          ></div>

          {message.documents && message.documents.length > 0 && (
            <ul style={MESSAGING_LIST_STYLES.attachmentList}>
              {message.documents.map((item, key) => (
                <li key={key}>
                  <button
                    className="btn btn-link download-btn-hover"
                    style={{
                      ...MESSAGING_LIST_STYLES.downloadButton,
                      ...(!isSend ? MESSAGING_LIST_STYLES.receiveDownloadButton : {}),
                    }}
                    onClick={() => handleDownloadFile(item)}
                  >
                    <i className={`fa ${isSend ? 'fa-file-text-o' : 'fa-file-text'}`} style={{ fontSize: "16px" }}></i>
                    <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 600 }}>
                      {item.documentName}
                    </span>
                    <i className="fa fa-arrow-circle-o-down" style={{ fontSize: "14px", opacity: 0.8 }}></i>
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div style={MESSAGING_LIST_STYLES.meta}>
            <div
              style={{
                ...MESSAGING_LIST_STYLES.time,
                ...(isSend ? MESSAGING_LIST_STYLES.sendTime : MESSAGING_LIST_STYLES.receiveTime),
              }}
            >
              <Moment format="h:mm a">{message.date}</Moment>
            </div>

            {isSend && (
              <div style={MESSAGING_LIST_STYLES.statusIcon}>
                {message.readStatus ? (
                  <i className="fa fa-check-circle" style={MESSAGING_LIST_STYLES.readIcon} title="Read"></i>
                ) : (
                  <i className="fa fa-check" style={MESSAGING_LIST_STYLES.unreadIcon} title="Sent"></i>
                )}
              </div>
            )}
          </div>
        </div>

        <style>
          {`
            .chat-content-reset p {
              margin-bottom: 0 !important;
            }
            .chat-content-reset *:last-child {
              margin-bottom: 0 !important;
            }
            .send-bubble-text * {
              color: #ffffff !important;
            }
            .send-bubble-text {
              color: #ffffff !important;
            }
            .chat-bubble-elevated:hover {
              transform: translateY(-1px);
              box-shadow: 0 6px 20px rgba(15, 23, 42, 0.1) !important;
            }
            .message-row-hover:hover .chat-avatar {
              transform: scale(1.05);
            }
            .download-btn-hover:hover {
              filter: brightness(1.05);
              text-decoration: none !important;
            }
          `}
        </style>
      </div>
    </div>
  );
};





export default MessagingList;

